/**
 * Date and string formatting utilities for LogViewer
 */

import { LOG_GROUP_LABELS } from './types';

// =============================================================================
// Date Formatting
// =============================================================================

export type TimestampFormat = 'full' | 'time' | 'short';

/**
 * Format a timestamp string for display
 *
 * @param timestamp - ISO timestamp string
 * @param format - Display format:
 *   - 'full': "Jan 22, 12:34:56 PM CST" (includes date and timezone)
 *   - 'time': "12:34:56 PM" (time only with AM/PM)
 *   - 'short': "12:34:56" (24-hour time only)
 * @returns Formatted timestamp string
 */
export function formatTimestamp(
  timestamp: string | Date | null,
  format: TimestampFormat = 'full',
): string {
  if (!timestamp) return '';

  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

  switch (format) {
    case 'full':
      return date.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
      });

    case 'time':
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });

    case 'short':
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });

    default:
      return date.toISOString();
  }
}

// =============================================================================
// String Formatting
// =============================================================================

/**
 * Get user-friendly label for a log group path
 *
 * @param logGroup - Full CloudWatch log group path
 * @returns Human-readable label or the original path if unknown
 */
export function getLogGroupLabel(logGroup: string): string {
  return LOG_GROUP_LABELS[logGroup] || logGroup;
}

/**
 * Format log stream name for display (extract the useful identifier part)
 *
 * Log stream names follow the pattern: "2024/01/21/[$LATEST]abc123..."
 * We extract just the identifier after [$LATEST] and truncate it.
 *
 * @param name - Full log stream name
 * @returns Truncated identifier (e.g., "abc123def012...")
 */
export function formatLogStreamName(name: string): string {
  // Extract just the identifier part after [$LATEST]
  const match = name.match(/\[\$LATEST\](.+)$/);
  if (match) {
    return match[1].slice(0, 12) + '...';
  }
  // Fallback: show last 15 characters
  return name.length > 15 ? '...' + name.slice(-15) : name;
}

/**
 * Format a request ID for display (truncate to first 8 characters)
 *
 * @param requestId - Full request ID (UUID format)
 * @param ungroupedLabel - Label to use for ungrouped logs
 * @returns Truncated request ID with ellipsis
 */
export function formatRequestId(
  requestId: string,
  ungroupedLabel = 'Ungrouped Logs',
): string {
  if (requestId === 'ungrouped') {
    return ungroupedLabel;
  }
  return requestId.slice(0, 8) + '...';
}

/**
 * Format a metric value for display
 *
 * @param value - Numeric value to format
 * @param precision - Decimal places for small values (|value| < 10)
 * @returns Formatted string
 */
export function formatMetricValue(
  value: number | undefined,
  precision = 1,
): string {
  if (value === undefined) return '';
  // Use absolute value for comparison to handle negative numbers correctly
  return Math.abs(value) < 10
    ? value.toFixed(precision)
    : Math.round(value).toString();
}
